import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getMyKeys, reportKeyLost, GetMyKeysOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Key, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

type Assignment = GetMyKeysOutputType['assignments'][0];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d.includes('T') ? d : d + 'T00:00:00'), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

export default function MyKeys() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [reporting, setReporting] = useState<string | null>(null);

  const load = () => {
    getMyKeys({})
      .then(d => setAssignments(d.assignments))
      .catch(() => toast.error('Failed to load keys'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (user) load(); }, [user]);

  const handleReport = async (id: string) => {
    setReporting(id);
    try {
      await reportKeyLost({ assignmentId: id });
      toast.success('Key reported as lost. Management has been notified.');
      load();
    } catch (e: any) { toast.error(e.message || 'Failed to report'); }
    finally { setReporting(null); }
  };

  if (loading) {
    return (
      <div className="px-6 py-8 max-w-xl mx-auto space-y-3">
        <Skeleton className="h-8 w-32" />
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}
      </div>
    );
  }

  return (
    <div className="px-6 py-8 max-w-xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
          <Key className="w-5 h-5 text-primary" /> My Keys
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Keys currently assigned to you</p>
      </div>

      {assignments.length === 0 ? (
        <div className="border border-border rounded-lg p-12 text-center">
          <Key className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground">No keys assigned</p>
          <p className="text-xs text-muted-foreground mt-1">Contact management if you need keys assigned to you</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg divide-y divide-border">
          {assignments.map(a => (
            <div key={a.id} className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {a.status === 'Lost' ? (
                  <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                )}
                <div>
                  <p className="text-sm font-medium text-foreground">{a.keyTypeName}</p>
                  <p className="text-xs text-muted-foreground">
                    Qty: {a.quantity} · Given {fmtDate(a.dateGiven)}
                    {a.status === 'Lost' && <span className="text-destructive ml-1">· Lost {fmtDate(a.lostDate)}</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded ${
                  a.status === 'Lost' ? 'bg-destructive/10 text-destructive' :
                  a.status === 'Returned' ? 'bg-muted text-muted-foreground' :
                  'bg-primary/10 text-primary'
                }`}>
                  {a.status}
                </span>
                {a.status === 'Active' && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-xs h-7">
                        Report Lost
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Report {a.keyTypeName} as Lost?</AlertDialogTitle>
                      </AlertDialogHeader>
                      <p className="text-sm text-muted-foreground px-6">
                        This will notify management. You may be responsible for the replacement cost.
                      </p>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleReport(a.id)}
                          className="bg-destructive text-destructive-foreground"
                        >
                          {reporting === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Report Lost'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
